import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CreditCard } from "lucide-react";
import { getAdminMe } from "../services/api";
import { activeCurrencySymbol } from "../utils/currency";

export interface SubscriptionInfo {
  subscriptionStatus?: "trial" | "active" | "past_due" | "canceled" | "expired";
  trialEndsAt?: string;
  currentPeriodEnd?: string;
  /** Stripe subscription (renews itself) vs one-time Razorpay payment. */
  autoRenews?: boolean;
  currentPlan?: {
    _id: string;
    name: string;
    durationMonths: number;
    price: number; // cents
  } | null;
}

/**
 * Loads the logged-in admin's plan details. /admin/me answers 402 once the
 * trial/subscription has lapsed, but still includes the details in `data`.
 */
export const useSubscriptionInfo = () => {
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAdminMe()
      .then((res) => {
        if (!cancelled) setInfo(res.data?.data || null);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 402) {
          setInfo(error.response.data?.data || null);
          setBlockedReason(error.response.data?.reason || null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { info, blockedReason, loading };
};

const DAY_MS = 24 * 60 * 60 * 1000;

const formatDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// Plans are charged in the platform currency set by master admin.
const formatPrice = (minor: number) => `${activeCurrencySymbol()}${(minor / 100).toFixed(2)}`;

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  trial: { label: "Free Trial", className: "bg-blue-100 text-blue-700" },
  active: { label: "Active", className: "bg-green-100 text-green-700" },
  past_due: { label: "Payment Due", className: "bg-yellow-100 text-yellow-800" },
  canceled: { label: "Canceled", className: "bg-gray-200 text-gray-700" },
  expired: { label: "Expired", className: "bg-red-100 text-red-700" },
};

/** Works out the plan name, relevant end date and whether it has passed. */
export const describeSubscription = (info: SubscriptionInfo) => {
  const status = info.subscriptionStatus || "trial";
  const isTrial = status === "trial";
  const endRaw = isTrial ? info.trialEndsAt : info.currentPeriodEnd;
  const endDate = endRaw ? new Date(endRaw) : null;
  const daysLeft = endDate
    ? Math.ceil((endDate.getTime() - Date.now()) / DAY_MS)
    : null;
  const isOver =
    ["expired", "canceled", "past_due"].includes(status) ||
    (daysLeft !== null && daysLeft < 0);

  const statusStyle =
    isOver && (status === "trial" || status === "active")
      ? STATUS_STYLES.expired
      : STATUS_STYLES[status] || STATUS_STYLES.trial;

  const planName = isTrial ? "Free Trial" : info.currentPlan?.name || "Subscription";

  let endLabel = isTrial ? "Trial ends on" : info.autoRenews === false ? "Valid until" : "Renews on";
  if (isOver) endLabel = isTrial ? "Trial ended on" : "Expired on";
  else if (status === "canceled") endLabel = "Access until";

  let remaining = "";
  if (daysLeft !== null) {
    if (daysLeft > 1) remaining = `${daysLeft} days left`;
    else if (daysLeft === 1) remaining = "1 day left";
    else if (daysLeft === 0) remaining = "Ends today";
    else remaining = `Ended ${Math.abs(daysLeft)} day${daysLeft === -1 ? "" : "s"} ago`;
  }

  return { status, isTrial, isOver, statusStyle, planName, endDate, endLabel, daysLeft, remaining };
};

interface SubscriptionSummaryProps {
  info: SubscriptionInfo;
  /** Show a "Manage plan" link to the billing page (hidden on that page itself). */
  showManageLink?: boolean;
}

const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
  info,
  showManageLink = false,
}) => {
  const { isOver, statusStyle, planName, endDate, endLabel, daysLeft, remaining } =
    describeSubscription(info);
  const plan = info.currentPlan;
  const endingSoon = !isOver && daysLeft !== null && daysLeft <= 7;

  return (
    <div className="rounded-lg border bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <p className="m-0 text-xs text-gray-500">Current plan</p>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-lg font-bold text-gray-800">{planName}</h2>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyle.className}`}
              >
                {statusStyle.label}
              </span>
            </div>
            {plan && (
              <p className="m-0 text-sm text-gray-500">
                {formatPrice(plan.price)} every {plan.durationMonths} month
                {plan.durationMonths > 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>

        {showManageLink && (
          <Link
            to="/admin/choose-plan"
            className="rounded-md border border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-600 no-underline transition hover:bg-blue-50"
          >
            {isOver ? "Choose a plan" : "Manage plan"}
          </Link>
        )}
      </div>

      {endDate && (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <CalendarClock className="h-4 w-4" />
            <span>
              {endLabel}{" "}
              <span className="font-semibold text-gray-800">{formatDate(endDate)}</span>
            </span>
          </div>
          {remaining && (
            <span
              className={`font-semibold ${
                isOver ? "text-red-600" : endingSoon ? "text-yellow-700" : "text-green-700"
              }`}
            >
              {remaining}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionSummary;
